import React from "react";

/*
 * Inline SVG diagrams for every [VISUAL n] block across the six enterprise-AI
 * posts. Design system tokens:
 *   navy  #0B1120  (used only as accent lines; page bg is the app card bg)
 *   blue  #3B82F6
 *   violet #8B5CF6
 *   slate #475569
 *   mono  font-mono (JetBrains Mono) for labels/overlines
 * All containers use rounded-xl (12px) borders, transparent fills.
 */

const NAVY = "#0B1120";
const BLUE = "#3B82F6";
const VIOLET = "#8B5CF6";
const SLATE = "#475569";

const Frame = ({ children, viewBox = "0 0 800 400", ariaLabel }) => (
  <svg
    viewBox={viewBox}
    role="img"
    aria-label={ariaLabel}
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: "100%", height: "auto", display: "block" }}
  >
    <defs>
      <linearGradient id="gGate" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor={VIOLET} stopOpacity="0.85" />
        <stop offset="1" stopColor={BLUE} stopOpacity="0.85" />
      </linearGradient>
      <linearGradient id="gCurve" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor={BLUE} stopOpacity="0.7" />
        <stop offset="1" stopColor={VIOLET} stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="gZoneOK" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#10B981" stopOpacity="0.55" />
        <stop offset="1" stopColor="#10B981" stopOpacity="0.15" />
      </linearGradient>
      <linearGradient id="gZoneWarn" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="#F59E0B" stopOpacity="0.15" />
        <stop offset="1" stopColor="#F59E0B" stopOpacity="0.6" />
      </linearGradient>
      <linearGradient id="gZoneBad" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor={VIOLET} stopOpacity="0.35" />
        <stop offset="1" stopColor={VIOLET} stopOpacity="0.85" />
      </linearGradient>
    </defs>
    {children}
  </svg>
);

const Caption = ({ children }) => (
  <figcaption className="mt-3 text-xs md:text-[13px] font-mono text-muted-foreground leading-relaxed max-w-2xl">
    {children}
  </figcaption>
);

const Wrap = ({ children, caption }) => (
  <figure className="my-10 rounded-xl border border-border bg-card/40 p-5 md:p-7">
    {children}
    <Caption>{caption}</Caption>
  </figure>
);

/* ---------- POST 1 · enterprise-ai-pilots-production ---------- */

const P1V1 = () => (
  <Wrap caption="Pilots don't fail at the end. They fail at a specific gate — and the gate predicts the fix.">
    <Frame ariaLabel="Four Gates funnel" viewBox="0 0 800 220">
      <rect x="10" y="90" width="60" height="40" rx="8" fill="none" stroke={SLATE} strokeWidth="1" />
      <text x="40" y="115" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>PILOTS</text>
      {["Ownership", "Workflow", "Economics", "Operations"].map((g, i) => {
        const x = 110 + i * 160;
        return (
          <g key={g}>
            <rect x={x} y="70" width="130" height="80" rx="12" fill="url(#gGate)" opacity={0.15 + i * 0.15} />
            <rect x={x} y="70" width="130" height="80" rx="12" fill="none" stroke="url(#gGate)" strokeWidth="1.5" />
            <text x={x + 12} y={92} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="9" fill={BLUE} opacity="0.9">GATE {i + 1}</text>
            <text x={x + 12} y={116} fontFamily="ui-sans-serif, system-ui" fontSize="15" fontWeight="600" fill="currentColor">{g}</text>
            <text x={x + 12} y={134} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="9" fill={SLATE}>attrition ↓</text>
            {i < 3 && <path d={`M${x + 132} 110 L${x + 148} 110`} stroke={SLATE} strokeWidth="1" markerEnd="url(#arrow)" />}
          </g>
        );
      })}
      <rect x="750" y="90" width="40" height="40" rx="8" fill="url(#gGate)" opacity="0.9" />
      <text x="770" y="115" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill="#fff">PROD</text>
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill={SLATE} />
        </marker>
      </defs>
    </Frame>
  </Wrap>
);

const P1V2 = () => {
  const cards = [
    { n: "01", t: "Ownership", test: "Two named humans: exec sponsor + workflow operator.", fail: "The innovation team is running it." },
    { n: "02", t: "Workflow", test: "A written after-state process document exists.", fail: "Users still complete the old process 'just in case.'" },
    { n: "03", t: "Economics", test: "Run-rate TCO/NPV modeled with sensitivity.", fail: "The business case is a slide, not a spreadsheet." },
    { n: "04", t: "Operations", test: "Failure runbook, paging, degradation, rollback.", fail: "'We'll figure out monitoring after launch.'" },
  ];
  return (
    <Wrap caption="The Four Gates — screen every pilot before funding, in this order.">
      <Frame ariaLabel="Four Gates cards" viewBox="0 0 800 420">
        {cards.map((c, i) => {
          const x = (i % 2) * 400 + 10;
          const y = Math.floor(i / 2) * 200 + 10;
          return (
            <g key={c.n}>
              <rect x={x} y={y} width="380" height="180" rx="12" fill="none" stroke={SLATE} strokeOpacity="0.4" strokeWidth="1" />
              <text x={x + 20} y={y + 30} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={BLUE}>GATE {c.n}</text>
              <text x={x + 20} y={y + 62} fontFamily="ui-sans-serif, system-ui" fontSize="20" fontWeight="600" fill="currentColor">{c.t}</text>
              <text x={x + 20} y={y + 92} fontFamily="ui-sans-serif, system-ui" fontSize="13" fill={SLATE}>Test:</text>
              <foreignObject x={x + 20} y={y + 100} width="340" height="40">
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: "ui-sans-serif,system-ui", fontSize: "13px", color: "currentColor", lineHeight: 1.4 }}>{c.test}</div>
              </foreignObject>
              <text x={x + 20} y={y + 152} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={VIOLET}>FAILURE SIGNATURE</text>
              <foreignObject x={x + 20} y={y + 156} width="340" height="30">
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: "ui-sans-serif,system-ui", fontSize: "12px", color: SLATE, lineHeight: 1.4, fontStyle: "italic" }}>{c.fail}</div>
              </foreignObject>
            </g>
          );
        })}
      </Frame>
    </Wrap>
  );
};

const P1V3 = () => (
  <Wrap caption="The Pilot Sprawl Index: active pilots ÷ trailing-12-month production launches.">
    <Frame ariaLabel="Pilot Sprawl Index gauge" viewBox="0 0 800 170">
      <rect x="30" y="70" width="240" height="30" rx="15" fill="url(#gZoneOK)" />
      <rect x="270" y="70" width="240" height="30" fill="url(#gZoneWarn)" />
      <rect x="510" y="70" width="260" height="30" rx="15" fill="url(#gZoneBad)" />
      <text x="150" y="55" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>CONVERTING · 0–3</text>
      <text x="390" y="55" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>ACCUMULATING · 3–6</text>
      <text x="640" y="55" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>SPRAWL · 6+</text>
      {[0, 2, 4, 6, 8, 10].map((n, i) => (
        <g key={n}>
          <line x1={30 + i * 148} y1="100" x2={30 + i * 148} y2="115" stroke={SLATE} strokeWidth="1" />
          <text x={30 + i * 148} y="130" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>{n}</text>
        </g>
      ))}
      <polygon points="510,75 522,60 534,75" fill={VIOLET} />
      <text x="522" y="150" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={VIOLET}>marker · 6</text>
    </Frame>
  </Wrap>
);

/* ---------- POST 2 · roi-modeling-ai-cfo ---------- */

const P2V1 = () => (
  <Wrap caption="The CFO-Grade Value Stack: cost takeout is bankable, capacity is conditional, optionality is strategic — model each on its own evidence standard.">
    <Frame ariaLabel="Value stack" viewBox="0 0 800 260">
      {[
        { t: "Layer 3 · Optionality (strategic)", sub: "Revenue upside, faster launches", certainty: "~30% certainty", fill: 0.2 },
        { t: "Layer 2 · Capacity release (conditional)", sub: "Hours ↦ headcount / throughput / backlog", certainty: "~55% certainty", fill: 0.5 },
        { t: "Layer 1 · Cost takeout (bankable)", sub: "Invoices, incidents, overtime", certainty: "~90% certainty", fill: 0.9 },
      ].map((l, i) => (
        <g key={l.t}>
          <rect x="30" y={20 + i * 70} width="740" height="60" rx="12" fill="url(#gGate)" opacity={l.fill} />
          <text x="50" y={45 + i * 70} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#fff" opacity="0.9">{l.t}</text>
          <text x="50" y={67 + i * 70} fontFamily="ui-sans-serif, system-ui" fontSize="13" fill="#fff" opacity="0.85">{l.sub}</text>
          <text x="750" y={45 + i * 70} textAnchor="end" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#fff" opacity="0.9">{l.certainty}</text>
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P2V2 = () => (
  <Wrap caption="One project, three evidence standards.">
    <Frame ariaLabel="Value stack table" viewBox="0 0 800 260">
      <rect x="10" y="10" width="780" height="40" rx="8" fill={NAVY} opacity="0.9" />
      {["Layer", "Evidence standard", "Modeling treatment", "Example"].map((h, i) => (
        <text key={h} x={30 + i * 195} y="36" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#fff">{h}</text>
      ))}
      {[
        ["1 · Cost takeout", "Invoices / incident logs", "Face value + lag", "License retirements"],
        ["2 · Capacity release", "Named conversion mechanism", "50–70% haircut", "Analyst hours ↦ backlog"],
        ["3 · Optionality", "Comparable / pilot signal", "Separate upside case", "Faster launches"],
      ].map((row, ri) => (
        <g key={ri}>
          <rect x="10" y={60 + ri * 65} width="780" height="60" rx="8" fill="none" stroke={SLATE} strokeOpacity="0.35" />
          {row.map((c, ci) => (
            <foreignObject key={ci} x={20 + ci * 195} y={70 + ri * 65} width="185" height="50">
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: ci === 0 ? "ui-monospace, JetBrains Mono, monospace" : "ui-sans-serif,system-ui", fontSize: "12px", color: ci === 0 ? BLUE : "currentColor", lineHeight: 1.4 }}>{c}</div>
            </foreignObject>
          ))}
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P2V3 = () => (
  <Wrap caption="No number enters the deck until it passes all five.">
    <Frame ariaLabel="Five tests" viewBox="0 0 800 260">
      {["Source", "Owner", "Sensitivity", "Symmetry", "Payback"].map((t, i) => (
        <g key={t}>
          <rect x={20 + i * 155} y="60" width="140" height="140" rx="12" fill="none" stroke={SLATE} strokeOpacity="0.4" />
          <circle cx={90 + i * 155} cy="95" r="14" fill="none" stroke={VIOLET} strokeWidth="1.5" />
          <text x={90 + i * 155} y="100" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill={VIOLET}>0{i + 1}</text>
          <text x={90 + i * 155} y="140" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontSize="14" fontWeight="600" fill="currentColor">{t}</text>
          <text x={90 + i * 155} y="160" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>test</text>
          <text x={90 + i * 155} y="185" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill={BLUE}>☐ pass</text>
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P2V4 = () => (
  <Wrap caption="The page CFOs actually read: where does this break?">
    <Frame ariaLabel="Sensitivity heatmap" viewBox="0 0 800 320">
      {["8%", "10%", "12%", "14%", "16%"].map((r, i) => (
        <text key={r} x="60" y={70 + i * 42} textAnchor="end" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>{r}</text>
      ))}
      {["70%", "80%", "90%", "100%", "110%"].map((r, i) => (
        <text key={r} x={110 + i * 125} y="35" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>{r}</text>
      ))}
      {(() => {
        const vals = [
          [154, 342, 529, 716, 903], [122, 299, 475, 652, 829],
          [93, 260, 426, 593, 760], [66, 224, 382, 539, 697], [41, 191, 340, 490, 640],
        ];
        return vals.map((row, ri) =>
          row.map((v, ci) => {
            const t = Math.min(1, v / 900);
            return (
              <g key={`${ri}-${ci}`}>
                <rect x={70 + ci * 125} y={50 + ri * 42} width="120" height="38" rx="4"
                  fill={t < 0.25 ? "#ef4444" : t < 0.5 ? "#f59e0b" : t < 0.75 ? BLUE : VIOLET}
                  fillOpacity={0.35 + t * 0.5} />
                <text x={130 + ci * 125} y={73 + ri * 42} textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="currentColor">${v}k</text>
              </g>
            );
          })
        );
      })()}
      <rect x="320" y="134" width="120" height="38" fill="none" stroke={VIOLET} strokeWidth="2" strokeDasharray="4 3" />
      <text x="70" y="290" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>x-axis: benefit realization · y-axis: discount rate · cell: NPV ($k)</text>
      <text x="70" y="305" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={VIOLET}>outlined cell = base case (12% · 100%)</text>
    </Frame>
  </Wrap>
);

/* ---------- POST 3 · rag-vs-fine-tuning-enterprise ---------- */

const P3V1 = () => (
  <Wrap caption="Locate the use case; the architecture follows.">
    <Frame ariaLabel="Volatility Specificity grid" viewBox="0 0 800 480">
      <line x1="100" y1="60" x2="100" y2="420" stroke={SLATE} strokeWidth="1" />
      <line x1="100" y1="420" x2="760" y2="420" stroke={SLATE} strokeWidth="1" />
      <text x="100" y="450" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>static</text>
      <text x="760" y="450" textAnchor="end" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>daily-changing</text>
      <text x="430" y="470" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>KNOWLEDGE VOLATILITY →</text>
      <text x="90" y="420" textAnchor="end" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>stock</text>
      <text x="90" y="70" textAnchor="end" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={SLATE}>specialized</text>
      <text x="60" y="240" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE} transform="rotate(-90 60 240)">↑ BEHAVIOR SPECIFICITY</text>
      {[
        { x: 250, y: 320, l: "Prompting is enough", sub: "low-V · low-S", c: SLATE },
        { x: 590, y: 320, l: "RAG", sub: "high-V · low-S", c: BLUE },
        { x: 250, y: 155, l: "Fine-tune", sub: "low-V · high-S", c: VIOLET },
        { x: 590, y: 155, l: "RAG + fine-tune", sub: "earn it (high-V · high-S)", c: "#EC4899" },
      ].map((q) => (
        <g key={q.l}>
          <rect x={q.x - 130} y={q.y - 55} width="260" height="110" rx="12" fill={q.c} fillOpacity="0.12" stroke={q.c} strokeOpacity="0.5" />
          <text x={q.x} y={q.y - 10} textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontSize="18" fontWeight="600" fill="currentColor">{q.l}</text>
          <text x={q.x} y={q.y + 20} textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={q.c}>{q.sub}</text>
        </g>
      ))}
      {[
        { x: 240, y: 340, l: "code assistant" },
        { x: 600, y: 340, l: "support bot" },
        { x: 250, y: 175, l: "brand copywriter" },
        { x: 580, y: 175, l: "contract analyst" },
      ].map((d) => (
        <g key={d.l}>
          <circle cx={d.x} cy={d.y} r="4" fill={BLUE} />
          <text x={d.x + 10} y={d.y + 4} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>{d.l}</text>
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P3V2 = () => (
  <Wrap caption="The real comparison is operational, not architectural.">
    <Frame ariaLabel="RAG vs fine-tune table" viewBox="0 0 800 300">
      <rect x="10" y="10" width="780" height="40" rx="8" fill={NAVY} opacity="0.9" />
      {["Dimension", "Prompting", "RAG", "Fine-tuning"].map((h, i) => (
        <text key={h} x={30 + i * 195} y="36" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#fff">{h}</text>
      ))}
      {[
        ["Update cost", "Instant", "Re-index", "Retrain run"],
        ["Time to first value", "Days", "Weeks", "Weeks–months"],
        ["Data prerequisite", "Examples", "Governed corpus", "1000s labeled examples"],
        ["Failure mode", "Prompt drift", "Retrieval miss", "Stale confident answers"],
      ].map((row, ri) => (
        <g key={ri}>
          <rect x="10" y={60 + ri * 55} width="780" height="50" rx="6" fill={ri === 0 ? VIOLET : "none"} fillOpacity={ri === 0 ? 0.08 : 0} stroke={SLATE} strokeOpacity="0.3" />
          {row.map((c, ci) => (
            <text key={ci} x={30 + ci * 195} y={90 + ri * 55} fontFamily={ci === 0 ? "ui-monospace, JetBrains Mono, monospace" : "ui-sans-serif,system-ui"} fontSize="12" fill={ci === 0 ? BLUE : "currentColor"}>{c}</text>
          ))}
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P3V3 = () => (
  <Wrap caption="Promote on evidence, not ambition.">
    <Frame ariaLabel="Escalation ladder" viewBox="0 0 800 300">
      {["Prompt", "RAG", "Fine-tune", "Both"].map((s, i) => {
        const x = 40 + i * 190;
        const h = 60 + i * 40;
        return (
          <g key={s}>
            <rect x={x} y={260 - h} width="150" height={h} rx="10" fill="url(#gGate)" opacity={0.25 + i * 0.2} />
            <text x={x + 75} y={280 - h / 2} textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontSize="16" fontWeight="600" fill="#fff">{s}</text>
            {i < 3 && (
              <>
                <path d={`M${x + 152} ${260 - h / 2} L${x + 188} ${260 - h / 2}`} stroke={SLATE} strokeWidth="1" markerEnd="url(#arrow2)" />
                <text x={x + 170} y={260 - h / 2 - 8} textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="9" fill={SLATE}>promote if</text>
              </>
            )}
          </g>
        );
      })}
      <defs>
        <marker id="arrow2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill={SLATE} />
        </marker>
      </defs>
      <text x="400" y="290" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>evidence-gated escalation · default to reversible</text>
    </Frame>
  </Wrap>
);

/* ---------- POST 4 · analytics-stack-saas-2026 ---------- */

const P4V1 = () => (
  <Wrap caption="MVAS: five layers, one job each. Any tool that can't name its layer doesn't get bought.">
    <Frame ariaLabel="MVAS layers" viewBox="0 0 800 260">
      {[
        { l: "1 · Collection", d: "Event tracking + tracking plan" },
        { l: "2 · Warehouse", d: "One analytical store of record" },
        { l: "3 · Transformation & Semantic", d: "Governed definitions — load-bearing", accent: true },
        { l: "4 · Consumption", d: "BI, dashboards, self-serve" },
        { l: "5 · Activation", d: "Metrics → CRM, alerts, lifecycle" },
      ].map((l, i) => (
        <g key={l.l}>
          <rect x="30" y={20 + i * 46} width="740" height="36" rx="10"
            fill={l.accent ? "url(#gCurve)" : "none"}
            stroke={l.accent ? "none" : SLATE}
            strokeOpacity={l.accent ? 0 : 0.4}
            fillOpacity={l.accent ? 1 : 0} />
          <text x="50" y={44 + i * 46} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill={l.accent ? "#fff" : BLUE}>{l.l}</text>
          <text x="750" y={44 + i * 46} textAnchor="end" fontFamily="ui-sans-serif, system-ui" fontSize="12" fill={l.accent ? "#fff" : "currentColor"}>{l.d}</text>
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P4V2 = () => (
  <Wrap caption="Governance Debt compounds until a definition layer caps it.">
    <Frame ariaLabel="Governance Debt curve" viewBox="0 0 800 300">
      <line x1="60" y1="250" x2="760" y2="250" stroke={SLATE} strokeWidth="1" />
      <line x1="60" y1="30" x2="60" y2="250" stroke={SLATE} strokeWidth="1" />
      <text x="400" y="285" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>TIME · TOOL COUNT →</text>
      <text x="45" y="140" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE} transform="rotate(-90 45 140)">↑ HRS LOST TO RECON</text>
      <path d="M60 245 Q220 240, 320 210 T500 100 Q560 60 620 100 T760 130" stroke="url(#gCurve)" strokeWidth="2.5" fill="none" />
      <line x1="500" y1="30" x2="500" y2="250" stroke={VIOLET} strokeDasharray="4 3" strokeWidth="1" />
      <text x="510" y="50" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={VIOLET}>semantic layer</text>
      <text x="510" y="66" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={VIOLET}>introduced ↓</text>
    </Frame>
  </Wrap>
);

const P4V3 = () => (
  <Wrap caption="A year to a stack that agrees with itself.">
    <Frame ariaLabel="Build order roadmap" viewBox="0 0 800 260">
      {["Q1", "Q2", "Q3", "Q4"].map((q, i) => (
        <text key={q} x={100 + i * 175} y="40" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill={BLUE}>{q}</text>
      ))}
      {[
        { q: 0, l: "Layers 1–2 + tracking plan" },
        { q: 1, l: "Layer 3 + governed dashboards slice" },
        { q: 2, l: "Layer 4 self-serve" },
        { q: 3, l: "Layer 5 activation" },
      ].map((s, i) => (
        <g key={i}>
          <rect x={30 + i * 175} y="70" width="150" height="100" rx="12" fill="url(#gGate)" opacity={0.2 + i * 0.15} />
          <foreignObject x={40 + i * 175} y="90" width="130" height="70">
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: "ui-sans-serif,system-ui", fontSize: "13px", color: "#fff", lineHeight: 1.4 }}>{s.l}</div>
          </foreignObject>
        </g>
      ))}
      <path d="M40 200 L760 200" stroke={SLATE} strokeDasharray="2 3" />
      <text x="400" y="225" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={SLATE}>ship thin slice of layer 4 alongside layer 3 — execs fund what they can see</text>
    </Frame>
  </Wrap>
);

/* ---------- POST 5 · intelligent-automation-where-to-start ---------- */

const P5V1 = () => (
  <Wrap caption="The three patterns where first-90-day automation ROI concentrates.">
    <Frame ariaLabel="Three patterns" viewBox="0 0 800 300">
      {[
        { t: "Document-to-Decision", ex: "AP · claims · KYC · contracts", hitl: "HITL @ confidence threshold" },
        { t: "Triage-and-Route", ex: "Support · SDR · IT · compliance", hitl: "HITL @ low-confidence route" },
        { t: "Reconcile-and-Alert", ex: "Month-end · OM · data quality", hitl: "HITL @ exception resolution" },
      ].map((p, i) => (
        <g key={p.t}>
          <rect x={20 + i * 260} y="20" width="240" height="260" rx="14" fill="none" stroke={SLATE} strokeOpacity="0.4" />
          <rect x={45 + i * 260} y="45" width="40" height="40" rx="10" fill="url(#gCurve)" opacity="0.85" />
          <text x={65 + i * 260} y="70" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="16" fill="#fff">0{i + 1}</text>
          <text x={40 + i * 260} y="120" fontFamily="ui-sans-serif, system-ui" fontSize="16" fontWeight="600" fill="currentColor">{p.t}</text>
          <foreignObject x={40 + i * 260} y="140" width="200" height="80">
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: "ui-sans-serif,system-ui", fontSize: "12px", color: SLATE, lineHeight: 1.5 }}>{p.ex}</div>
          </foreignObject>
          <text x={40 + i * 260} y="255" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={VIOLET}>{p.hitl}</text>
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P5V2 = () => (
  <Wrap caption="Four questions; a 'no' at any gate means pick a different first target — not a different program.">
    <Frame ariaLabel="Selection funnel" viewBox="0 0 800 340">
      {[
        { q: "Q1 · Volume: 500+/month?" },
        { q: "Q2 · Rules: learnable in a week?" },
        { q: "Q3 · Data: inputs digital?" },
        { q: "Q4 · Owner: process owner wants it?" },
      ].map((s, i) => {
        const w = 520 - i * 60;
        const x = (800 - w) / 2 - 60;
        return (
          <g key={i}>
            <rect x={x} y={20 + i * 75} width={w} height="55" rx="12" fill="url(#gGate)" opacity={0.2 + i * 0.15} />
            <text x={x + 20} y={53 + i * 75} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill="#fff">{s.q}</text>
            <path d={`M${x + w} ${47 + i * 75} L${x + w + 40} ${47 + i * 75}`} stroke="#ef4444" strokeWidth="1.5" markerEnd="url(#arrowRed)" />
            <text x={x + w + 45} y={51 + i * 75} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill="#ef4444">pick a different target</text>
          </g>
        );
      })}
      <text x="400" y="330" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={BLUE}>4 × YES ↓  green-lit first target</text>
      <defs>
        <marker id="arrowRed" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill="#ef4444" />
        </marker>
      </defs>
    </Frame>
  </Wrap>
);

const P5V3 = () => (
  <Wrap caption="Trust is built in shadow mode; value is claimed with the baseline.">
    <Frame ariaLabel="90 day ladder" viewBox="0 0 800 260">
      <line x1="40" y1="200" x2="760" y2="200" stroke={SLATE} strokeWidth="1" />
      {["Weeks 1–2\nBaseline", "Weeks 3–6\nShadow", "Weeks 7–10\nAssisted", "Weeks 11–13\nProduction + number"].map((s, i) => {
        const x = 60 + i * 180;
        return (
          <g key={i}>
            <rect x={x} y="140" width="160" height="60" rx="10" fill="url(#gGate)" opacity={0.2 + i * 0.18} />
            <foreignObject x={x + 10} y="145" width="145" height="55">
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: "ui-monospace, JetBrains Mono, monospace", fontSize: "11px", color: "#fff", whiteSpace: "pre-line", lineHeight: 1.35 }}>{s}</div>
            </foreignObject>
          </g>
        );
      })}
      <path d="M60 100 Q240 90, 400 70 T760 30" stroke={VIOLET} strokeWidth="2" fill="none" />
      <text x="60" y="90" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={VIOLET}>agreement rate ↑</text>
      <line x1="400" y1="45" x2="400" y2="140" stroke={BLUE} strokeDasharray="3 3" />
      <text x="410" y="55" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="10" fill={BLUE}>value starts here → wk 7</text>
    </Frame>
  </Wrap>
);

/* ---------- POST 6 · mcp-agentic-enterprise-workflows ---------- */

const P6V1 = () => (
  <Wrap caption="The Agentic Integration Stack: agents are the top layer; the durable investment is the two layers beneath them.">
    <Frame ariaLabel="Agentic stack" viewBox="0 0 800 300">
      {[
        { l: "Layer 4 · Agents", d: "Task executors — replaceable" },
        { l: "Layer 3 · Orchestration & Governance", d: "Permissions · audit · approval gates" },
        { l: "Layer 2 · Tool Interface (MCP servers)", d: "The N+M layer — compounding returns", accent: true },
        { l: "Layer 1 · Systems of Record", d: "Existing platforms — now invocable" },
      ].map((l, i) => (
        <g key={l.l}>
          <rect x="30" y={20 + i * 68} width="740" height="56" rx="10"
            fill={l.accent ? "url(#gCurve)" : "none"} fillOpacity={l.accent ? 1 : 0}
            stroke={l.accent ? "none" : SLATE} strokeOpacity={l.accent ? 0 : 0.4} />
          <text x="50" y={44 + i * 68} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill={l.accent ? "#fff" : BLUE}>{l.l}</text>
          <text x="50" y={62 + i * 68} fontFamily="ui-sans-serif, system-ui" fontSize="12" fill={l.accent ? "#fff" : "currentColor"} opacity={l.accent ? 0.95 : 0.9}>{l.d}</text>
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P6V2 = () => (
  <Wrap caption="The economics of a protocol: integrations you build once instead of per pair.">
    <Frame ariaLabel="NxM vs N+M" viewBox="0 0 800 360">
      <text x="200" y="30" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill={SLATE}>N × M — 5 models × 8 systems = 40 links</text>
      <text x="600" y="30" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill={VIOLET}>N + M — via protocol = 13 links</text>
      {(() => {
        const nodes = [];
        for (let i = 0; i < 5; i++) nodes.push({ x: 80, y: 70 + i * 45, side: "L", type: "M" });
        for (let j = 0; j < 8; j++) nodes.push({ x: 320, y: 60 + j * 32, side: "R", type: "S" });
        return (
          <>
            {nodes.map((n, i) => (
              <g key={i}>
                <circle cx={n.x} cy={n.y} r="8" fill={n.type === "M" ? BLUE : SLATE} />
                <text x={n.x + (n.side === "L" ? -12 : 12)} y={n.y + 4} textAnchor={n.side === "L" ? "end" : "start"} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="9" fill={SLATE}>{n.type}{i + 1 - (n.side === "R" ? 5 : 0)}</text>
              </g>
            ))}
            {[...Array(5)].flatMap((_, i) => [...Array(8)].map((__, j) => (
              <line key={`${i}-${j}`} x1="80" y1={70 + i * 45} x2="320" y2={60 + j * 32} stroke={SLATE} strokeOpacity="0.15" strokeWidth="0.6" />
            )))}
          </>
        );
      })()}
      <circle cx="600" cy="180" r="30" fill="url(#gCurve)" />
      <text x="600" y="185" textAnchor="middle" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill="#fff">MCP</text>
      {[...Array(5)].map((_, i) => (
        <g key={`m${i}`}>
          <circle cx="480" cy={70 + i * 45} r="8" fill={BLUE} />
          <line x1="488" y1={70 + i * 45} x2="572" y2="180" stroke={VIOLET} strokeOpacity="0.7" strokeWidth="1" />
        </g>
      ))}
      {[...Array(8)].map((_, i) => (
        <g key={`s${i}`}>
          <circle cx="720" cy={60 + i * 32} r="8" fill={SLATE} />
          <line x1="712" y1={60 + i * 32} x2="628" y2="180" stroke={VIOLET} strokeOpacity="0.7" strokeWidth="1" />
        </g>
      ))}
    </Frame>
  </Wrap>
);

const P6V3 = () => (
  <Wrap caption="Four conditions, no partial credit.">
    <Frame ariaLabel="Agent Readiness Test" viewBox="0 0 800 280">
      {[
        { c: "01 · Multi-step with tool use", fail: "→ script / RAG" },
        { c: "02 · Bounded blast radius", fail: "→ add approval gate" },
        { c: "03 · Verifiable outcome", fail: "→ add checkpoints" },
        { c: "04 · Volume or urgency", fail: "→ leave manual" },
      ].map((r, i) => (
        <g key={r.c}>
          <rect x="30" y={20 + i * 60} width="740" height="45" rx="10" fill="none" stroke={SLATE} strokeOpacity="0.4" />
          <rect x="45" y={33 + i * 60} width="18" height="18" rx="4" fill="none" stroke={BLUE} strokeWidth="1.5" />
          <text x="75" y={48 + i * 60} fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="12" fill="currentColor">{r.c}</text>
          <text x="750" y={48 + i * 60} textAnchor="end" fontFamily="ui-monospace, JetBrains Mono, monospace" fontSize="11" fill={VIOLET}>fail {r.fail}</text>
        </g>
      ))}
    </Frame>
  </Wrap>
);

/* ---------- registry ---------- */

const VISUALS = {
  "enterprise-ai-pilots-production": [P1V1, P1V2, P1V3],
  "roi-modeling-ai-cfo": [P2V1, P2V2, P2V3, P2V4],
  "rag-vs-fine-tuning-enterprise": [P3V1, P3V2, P3V3],
  "analytics-stack-saas-2026": [P4V1, P4V2, P4V3],
  "intelligent-automation-where-to-start": [P5V1, P5V2, P5V3],
  "mcp-agentic-enterprise-workflows": [P6V1, P6V2, P6V3],
};

export function BlogVisual({ slug, n }) {
  const list = VISUALS[slug];
  if (!list) return null;
  const Comp = list[n - 1];
  return Comp ? <Comp /> : null;
}
